import { PhoneInput, PhoneInputOnBlurChange, PhoneInputProps, Textarea, TextareaProps, TextInput, TextInputProps } from "@centricsoftware/design-system";
import { FORMAT_PHONE, FORMAT_TEXTAREA } from "c8-platform";
import { CLEAR_RES } from "csi/components/constants";
import { C8FieldValue } from "csi/typings";
import { trimAndPurifyText } from "csi/utility/stringUtility";
import { isString } from "lodash";
import React, { ChangeEvent, ChangeEventHandler, FunctionComponent, Ref, useEffect, useState } from "react";

/**
 * TODO: (some may be hidden on forms)
 * Consider supporting the following formats:
 * Alphanumeric (C8 internally Alphanumeric)
 * Barcode (C8 internally Barcode)
 * Email Address (C8 internally Email)
 * Hyperlink (C8 internally URL)
 * Latest Multi-line Comment (C8 internally CommentLatest)
 * Multi-line Comment (C8 internally Comment)
 * Multi-line Text (C8 internally Textarea)
 * Phone (C8 internally Phone)
 * Rich Text (C8 internally Rich)
 */

// Internal C8 String Formats:
// "Alphanumeric",
// "Barcode",
// "Comment",
// "CommentLatest",
// "Email",
// "Phone",
// "Rich",
// "Textarea",
// "URL"

// Internal DS types:
// type?: "barcode" | "email" | "hidden" | "readonly" | "text";

// Notes for phone input on change parameters:
// PhoneInputOnChange = ((data: { country: ParsedCountry; inputValue: string; phone: string }) => void);

const mapC8InternalFormatToDSType = {
	Alphanumeric: "text", // Alphanumeric
	Barcode: "barcode", // Barcode
	Comment: "text", // Multi-line Comment
	CommentLatest: "text", // Latest Multi-line Comment
	Email: "email", // Email Address
	Phone: "phone", // Phone
	Rich: "rich", // Rich Text
	Textarea: "textarea", // Multi-line Text
	URL: "text", // Hyperlink
};

export interface StringComponentProps extends Omit<(TextInputProps & PhoneInputProps & TextareaProps), "ref" | "onChange"> {
	format?: string;
	onChange?: (value?: string, event?: ChangeEvent<HTMLElement>) => void;
	ref?: Ref<HTMLElement>;
}

// Function to handle transforming value into pure text
function transformString(value: C8FieldValue, format?: string): string {
	if (!isString(value)) {
		console.warn("StringComponent: value is not a string", { value });
		value = String(value);
	}
	return trimAndPurifyText(value, format, true);
};

const StringComponent: FunctionComponent<StringComponentProps> = ({ format, onChange, ref, value, ...props }) => {
	// Initialize pureText using the foo function
	const [pureText, setPureText] = useState(transformString(value, format));

	// Update pureText when the value changes
	useEffect(() => {
		setPureText(transformString(value, format));
	}, [value, format]);

	// TODO: split phone component into separate component, and support all use cases
	if (format === FORMAT_PHONE) {
		const onPhoneInputChange: PhoneInputOnBlurChange = (_, event?: ChangeEvent<HTMLElement>) => {
			onChange?.(null, event);
		};
		return (
			<PhoneInput
				clearButtonAriaLabelRes={CLEAR_RES}
				value={pureText}
				{...props as PhoneInputProps}
				onBlurChange={onPhoneInputChange}
				ref={ref as Ref<HTMLInputElement>}
			/>
		);
	}

	if (format === FORMAT_TEXTAREA) {
		const onTextareaChange: ChangeEventHandler<HTMLTextAreaElement> = (event) => {
			onChange?.(null, event);
		};
		return (
			<Textarea
				value={pureText}
				{...props as TextareaProps}
				onChange={onTextareaChange}
				ref={ref as Ref<HTMLTextAreaElement>}
			/>
		);
	}

	const onTextInputChange: ChangeEventHandler<HTMLInputElement> = (event) => {
		onChange?.(null, event);
	};

	return (
		<TextInput
			type={mapC8InternalFormatToDSType[format] || "text"}
			value={pureText}
			{...props as TextInputProps}
			onChange={onTextInputChange}
			ref={ref as Ref<HTMLInputElement>}
		/>
	);
};


export default StringComponent;
